import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { changePassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plane, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ChangePasswordPage = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    if (newPassword === currentPassword) {
      toast.error('A nova senha deve ser diferente da atual');
      return;
    }

    setLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      updateUser({ ...user, must_change_password: false });
      toast.success('Senha alterada com sucesso!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
  <div className="min-h-screen flex flex-col md:flex-row">
    <div
      className="hidden md:block md:w-1/2 relative bg-cover bg-center"
      style={{ backgroundImage: 'url("/img/Simulados-VOAR.png")' }}
    >
      <div className="absolute inset-0 flex flex-col justify-end p-12">
          <h1 className="text-5xl font-black text-white mb-4">
            Simulados V.O.A.R.
          </h1>
          <p className="text-xl text-zinc-300 max-w-md">
            Defina uma nova senha segura para sua conta.
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className="auth-form bg-zinc-100">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <img src="/logo-voar.png" alt="Simulados V.O.A.R." className="h-12" />
            <div>
              <h2 className="text-lg font-bold text-zinc-900">Simulados V.O.A.R.</h2>
              <p className="text-xs text-zinc-500">Simulados exclusivos para alunos do Método V.O.A.R.</p>
            </div>
          </div>

          {/* Alert */}
          <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg mb-8">
            <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5" />
            <div>
              <p className="font-medium text-orange-800">Atenção</p>
              <p className="text-sm text-orange-700">
                Por segurança, você precisa definir uma nova senha antes de continuar.
              </p>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-3xl font-bold text-zinc-900 mb-2">
            Alterar Senha
          </h3>
          <p className="text-zinc-500 mb-8">
            Defina uma nova senha para sua conta.
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="currentPassword" className="form-label">Senha Atual</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                  required
                  data-testid="current-password-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="newPassword" className="form-label">Nova Senha</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                  required
                  data-testid="new-password-input"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">Mínimo de 6 caracteres</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="form-label">Confirmar Nova Senha</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 z-10" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-12 bg-white border border-zinc-200 rounded-lg focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium rounded-lg btn-press"
              disabled={loading}
              data-testid="change-password-btn"
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
